import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { MOSAIC_DEFAULTS, planMosaic, safeAspect, type MosaicConfig, type MosaicLayout, type MosaicTile } from './mosaicMath';

/*
 * Оболочка вокруг чистой раскладки (mosaicMath.ts): меряет свою область и ставит
 * плитки по посчитанным координатам.
 *
 * Плитки лежат абсолютно, поэтому их размеры не влияют на размер контейнера —
 * ResizeObserver не может войти в круг «посчитали — переставили — посчитали».
 * Ключ плитки — идентификатор участника: при смене раскладки React меняет
 * только стиль, видеоэлемент остаётся прежним и дорожка не перецепляется.
 */

export type MosaicEntry = {
  id: string;
  /** Пропорция кадра; пусто — возьмётся значение по умолчанию. */
  aspect?: number | null;
};

/** Размер области, пока она ещё не измерена. */
const UNMEASURED = { w: 0, h: 0 };

export function Mosaic({
  items,
  render,
  config,
  className,
}: {
  items: MosaicEntry[];
  /** Чем рисовать одну плитку: сам участник решает, что у него внутри. */
  render: (id: string, tile: MosaicTile) => ReactNode;
  config?: Partial<MosaicConfig>;
  className?: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(UNMEASURED);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const read = (w: number, h: number) => {
      // Дробные доли ширины от масштабирования страницы дают поток ложных
      // пересчётов: округляем до точки, раскладка от этого не страдает.
      const next = { w: Math.round(w), h: Math.round(h) };
      setSize((cur) => (cur.w === next.w && cur.h === next.h ? cur : next));
    };
    // Меряется внутренний слой без собственных отступов: у внешнего они есть
    // (под шапкой звонка и панелью управления), и на нём `contentRect` пришёл
    // бы вместе с ними. Первый замер берём сами: полагаться на то, что
    // наблюдатель отдаст размер сразу при подписке, нельзя — стенд поймал
    // случай, когда плитки не появлялись вовсе.
    read(el.clientWidth, el.clientHeight);
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) read(r.width, r.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Прошлая раскладка нужна расчёту, но не должна перезапускать сам расчёт:
  // держим её в ссылке, иначе каждый результат тянул бы следующий пересчёт.
  const prev = useRef<MosaicLayout | null>(null);
  const shape = items.map((it) => `${it.id}:${safeAspect(it.aspect).toFixed(3)}`).join(',');

  const layout = useMemo(() => {
    const next = planMosaic({
      container: size,
      items: items.map((it) => ({ id: it.id, aspect: safeAspect(it.aspect) })),
      config,
      prev: prev.current,
    });
    prev.current = next;
    return next;
    // shape заменяет items: важен состав и пропорции, а не ссылка на массив
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size.w, size.h, shape, config]);

  const byId = new Map(layout.tiles.map((t) => [t.id, t]));

  return (
    <div className={`mosaic${className ? ` ${className}` : ''}`}>
      <div ref={box} className="mosaic-area">
        {items.map((it) => {
          const tile = byId.get(it.id);
          if (!tile) return null;
          const style: CSSProperties = {
            left: `${tile.x}px`,
            top: `${tile.y}px`,
            width: `${tile.w}px`,
            height: `${tile.h}px`,
          };
          return (
            <div key={it.id} className="mosaic-cell" style={style} data-fit={tile.fit}>
              {render(it.id, tile)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Пропорции кадров, собранные с самих видеоэлементов: тип устройства тут ни
 * при чём, важен фактический размер дорожки. Пока метаданных нет, участник
 * идёт с пропорцией по умолчанию, а выключенная камера оставляет последнюю
 * известную — плитка не меняет форму, когда человек гасит видео.
 */
export function useAspects() {
  const [aspects, setAspects] = useState<Record<string, number>>({});
  const report = useCallback((id: string, aspect: number) => {
    const next = safeAspect(aspect);
    setAspects((cur) => {
      const was = cur[id];
      // Дрожание в доли процента (адаптивный битрейт меняет размер кадра) не
      // должно перекладывать сетку.
      if (was && Math.abs(was - next) / was < 0.02) return cur;
      return { ...cur, [id]: next };
    });
  }, []);
  return { aspects, report };
}

export { MOSAIC_DEFAULTS };
